import styled from "@emotion/styled";
import { Link } from "gatsby";
import React from "react";
import theme from "../../styles/theme";
import CTA from "../cta/cta";
import { default as CTAComponent } from "../cta/cta";
import { Button as LoftButton, Panel, TabView } from "@xometry/xometry_loft";
import { css } from "@emotion/css";

interface IToggleButtonProps {
  isDarkMode: boolean;
  transitionPercent: number;
}

export const HeroBifurcatedBase = styled.section<IToggleButtonProps>`
  padding: 2rem 1rem;
  transition: background 250ms ease-in-out;
  position: relative;
  overflow: hidden;

  *,
  :after,
  :before {
    box-sizing: border-box;
  }

  ${theme.largerThan.lg} {
    padding: 3rem 2rem 2rem;
  }

  :before {
    content: "";
    position: absolute;
    width: 600px;
    height: 600px;
    left: -100%;
    top: -10%;
    background-image: url(/${(props) =>
      props.isDarkMode ? "tee-line-art.svg" : "tee.webp"});
    background-repeat: no-repeat;
    background-size: cover;
    opacity: 0.1;
    transition: all 0.2s ease-out;

    ${theme.largerThan.md} {
      top: -5%;
      left: -50%;
    }

    ${theme.largerThan.lg} {
      top: -5%;
      left: -40%;
      opacity: 1;
    }

    @media (min-width: 1100px) {
      left: -32%;
    }

    ${theme.largerThan.xl} {
      top: 0;
      left: -29%;
    }

    ${theme.largerThan["2xl"]} {
      left: -18%;
    }

    ${theme.largerThan["3xl"]} {
      left: -7%;
    }
  }

  :after {
    content: "";
    position: absolute;
    width: 600px;
    height: 600px;
    right: -100%;
    top: 15%;
    background-image: url(/${(props) =>
      props.isDarkMode ? "x-line-art.svg" : "x.webp"});
    background-repeat: no-repeat;
    background-size: cover;
    opacity: 0.1;
    transition: all 0.2s ease-out;

    ${theme.largerThan.md} {
      top: -7%;
      right: -50%;
    }

    ${theme.largerThan.lg} {
      top: -10%;
      right: -40%;
      opacity: 1;
    }

    @media (min-width: 1100px) {
      right: -32%;
    }

    ${theme.largerThan.xl} {
      top: 0;
      right: -29%;
    }

    ${theme.largerThan["2xl"]} {
      right: -18%;
    }

    ${theme.largerThan["3xl"]} {
      right: -10%;
    }
  }

  .container {
    position: relative;
    z-index: 2;
  }
`;

export const HeroBifurcatedContent = styled.div<IToggleButtonProps>`
  max-width: ${theme.breakpoints[2]}px;
  margin: 0 auto ${theme.spacing[8]};
  display: grid;
  gap: ${theme.spacing[8]};
  justify-items: center;
  text-align: center;
  ${(props) =>
    props.transitionPercent > 0.5 &&
    props.isDarkMode &&
    `color: ${theme.colors.white};`}

  ${theme.largerThan["2xl"]} {
    max-width: ${theme.narrowWidth};
    padding: 0 ${theme.spacing[8]};
  }

  h1 {
    display: grid;
    align-content: end;
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: ${theme.fontSizes[8]};
    ${(props) =>
      props.transitionPercent > 0.5 &&
      props.isDarkMode &&
      `color: ${theme.colors.white};`}
  }

  .intro {
    max-width: 700px;
    margin: 0 auto;
    font-size: ${theme.fontSizes[1]};
    ${(props) =>
      props.transitionPercent > 0.5 &&
      props.isDarkMode &&
      `color: ${theme.colors.darkBlues[25]};`}

    ${theme.largerThan.lg} {
      font-size: ${theme.fontSizes[3]};
    }
  }

  h4 {
    ${(props) =>
      props.transitionPercent > 0.5 &&
      props.isDarkMode &&
      `color: ${theme.colors.white};`}
  }
`;

export const Toggle = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
`;

export const ToggleLabel = styled.span`
  width: 130px;

  ${theme.largerThan.sm} {
    width: 240px;
  }

  &[aria-label="Low Volume Manufacturing"] {
    text-align: right;
  }

  &[aria-label="Production"] {
    text-align: left;
  }
`;

export const ToggleButton = styled.button<IToggleButtonProps>`
  width: 55px;
  height: 28px;
  padding: 0;
  border: 1px solid
    ${(props) =>
      props.isDarkMode
        ? theme.colors.darkBlues[25]
        : theme.colors.darkBlues[50]};
  border-radius: 14px;
  background-color: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: rgb(71, 97, 117, 0.1);
  }

  &:before {
    content: "";
    display: block;
    width: 20px;
    height: 20px;
    border-radius: 20px;
    background-color: ${theme.colors.green};
    transition: all 250ms ease-in-out;
    transform: translateX(${(props) => (props.isDarkMode ? "29px" : "3px")});
  }

  span {
    display: none;
  }
`;

export const HeroBifurcatedCTAs = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  ${theme.largerThan.md} {
    flex-direction: row;
  }

  a {
    color: ${theme.colors.darkBlues[100]};
    font-weight: ${theme.fontWeights.semibold};
    text-decoration: none;
  }
`;

export const StyledCTA = styled(LoftButton)``;

export const Certifications = styled.ul<IToggleButtonProps>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  justify-content: center;
  margin: 0 ${theme.spacing[8]};
  padding: 0;
  font-size: ${theme.fontSizes[0]};

  ${theme.largerThan.lg} {
    flex-direction: row;
    align-items: center;
  }

  li {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    ${(props) =>
      props.transitionPercent > 0.5 &&
      props.isDarkMode &&
      `color: ${theme.colors.darkBlues[25]};`}

    ${(props) =>
      props.transitionPercent > 0.5 &&
      props.isDarkMode &&
      `
      svg path {
        fill: ${theme.colors.darkBlues[25]};
      }
    `}

    ${theme.largerThan.lg} {
      align-items: center;
    }
  }
`;

export const Procecess = styled.div<IToggleButtonProps>`
  list-style: none;
  margin: ${theme.spacing[8]} 0;
  padding: 0;
  position: relative;

  ${theme.largerThan.md} {
    display: flex;
    gap: ${theme.spacing[8]};
    flex-wrap: wrap;
    justify-content: center;
    align-items: top;
  }

  ${(props) => props.isDarkMode && `color: ${theme.colors.white};`}
`;

export const ProcessItem = styled.div<IToggleButtonProps>`
  position: relative;

  ${theme.smallerThan.md} {
    display: flex !important;
    gap: ${theme.spacing[2]};
    margin: ${theme.spacing[2]} 0;
    padding: ${theme.spacing[2]} ${theme.spacing[4]};
    border-radius: ${theme.borderRadius};
    background-color: ${theme.colors.white};
  }

  ${theme.largerThan.md} {
    display: grid !important;
    grid-auto-columns: max-content;
    justify-items: center;
  }

  a {
    display: grid;
    gap: 0.5rem;
    justify-items: center;
    font-size: ${theme.fontSizes[1]};
    font-weight: ${theme.fontWeights.semibold};
    color: ${(props) =>
      props.transitionPercent > 0.5 && props.isDarkMode
        ? theme.colors.darkBlues[25]
        : theme.colors.darkBlues[100]};
    text-decoration: none;

    span {
      text-align: center;
    }
  }

  .cardCTA {
    transition: all 0.3s ease-out;

    ${theme.largerThan.lg} {
      position: absolute;
      opacity: 0;
    }
  }

  &:hover {
    .cardCTA {
      opacity: 1;
    }
  }
`;

export const QuoteCTATitle = styled.h3`
  margin: 0;
  text-align: center;
  padding: 0 16px;
  font-size: ${theme.fontSizes[2]};
  font-weight: ${theme.fontWeights.semibold};
  letter-spacing: 0;
  line-height: ${theme.leading.normal};
  color: ${theme.colors.darkBlues[100]};
  ${theme.smallerThan.md} {
    padding: 0 8px;
  }
  ${theme.smallerThan.sm} {
    font-size: ${theme.fontSizes[1]};
    padding: 0;
  }
`;

export const QuoteCTABackground = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 0px;
  padding-bottom: 0px;
  margin: 18px auto 0 auto;
`;

export const UploadFileContainer = styled.div`
  position: fixed;
  z-index: 9999;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);

  display: flex;
  justify-content: center;
`;

export const CardPrimaryCTA = styled(CTAComponent)<{}>`
  ${theme.smallerThan.xl} {
    display: none;
  }
  ${theme.largerThan.xl} {
    bottom: -30%;
  }
`;

export const CardSecondaryCTA = styled(CTAComponent)<{}>`
  ${theme.largerThan.lg} {
    bottom: -30%;
  }
  ${theme.largerThan.xl} {
    bottom: -60%;
  }
`;

export const CardLink = styled.a<IToggleButtonProps>`
  display: grid;
  gap: 0.5rem;
  justify-items: center;
  font-size: ${theme.fontSizes[1]};
  font-weight: ${theme.fontWeights.semibold};
  color: ${(props) =>
    props.transitionPercent > 0.5 && props.isDarkMode
      ? theme.colors.darkBlues[25]
      : theme.colors.darkBlues[100]};
  text-decoration: none;
  \ ::after {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    content: " ";
  }
`;

export const CardTitle = styled.span``;

export const CardImage = styled.div`
  display: flex;
  justify-content: center;
  ${theme.smallerThan.sm} {
    width: 36px;
    order: -1;
    margin-right: 16px;
    height: 100%;
    flex: 0 1 auto;
  }
`;

export const CardClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export const ClickableCardArea = styled.div`
  position: relative;

  ${theme.smallerThan.md} {
    /* Cards are rows */
    display: flex !important;
    flex-direction: row;
    flex: 1 1 auto;
    align-items: center;
  }

  .gatsby-image-wrapper {
    transition: transform 0.2s ease-out;
  }

  &:hover {
    .gatsby-image-wrapper {
      transform: translateY(-5px);
    }
    a {
      color: ${theme.colors.blues[100]};
    }
  }
`;

export const HeroCTAs = styled.div`
  display: grid;
  gap: ${theme.spacing[4]};
  text-align: initial;
`;

export const FormOverlay = styled.div`
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(6px);
`;

export const FormContainer = styled.div`
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  box-shadow: ${theme.boxShadow.small};

  &:before {
    position: absolute;
    z-index: -1;
    top: 50%;
    left: 50%;
    margin-left: -25px;
    margin-top: -25px;
    content: '';
    display: block;
    width: 50px;
    aspect-ratio: 1;
    border-radius: 50%;
    background: 
      radial-gradient(farthest-side,#fff 94%,#0000) top/8px 8px no-repeat,
      conic-gradient(#0000 30%,#fff);
    -webkit-mask: radial-gradient(farthest-side,#0000 calc(100% - 8px),#000 0);
    animation: spinner 1s infinite linear;
    }

    @keyframes spinner { 
      100%{transform: rotate(1turn)}
    }
  }
`;

export const FormContent = styled.div`
  max-width: 700px;
  background-color: ${theme.colors.white};
  padding: ${theme.spacing[8]};
`;

export const SliderClass = css`
  width: 100%;

  ${theme.largerThan.md} {
    -webkit-mask-image: linear-gradient(
      90deg,
      transparent 3%,
      rgba(0, 0, 0, 1) 10%,
      rgba(0, 0, 0, 1) 90%,
      transparent 97%
    );
  }

  ${theme.largerThan.md} {
    .slick-list {
      padding-bottom: 50px !important;
    }
  }

  .slick-track {
    height: initial !important;
    padding-bottom: 24px;

    ${theme.largerThan.md} {
      .slick-slide {
        padding: ${theme.spacing[4]};
      }
    }
  }
`;

export const SliderRightArrowContainer = styled.div`
  position: absolute;
  color: ${theme.colors.blues[100]};
  top: 0;
  right: -5%;
  bottom: 0;
  z-index: 3;
  width: 125px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${theme.smallerThan.md} {
    display: none !important;
  }
`;

export const SliderLeftArrowContainer = styled.div`
  position: absolute;
  color: ${theme.colors.blues[100]};
  top: 0;
  left: -5%;
  bottom: 0;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 125px;
  ${theme.smallerThan.md} {
    display: none !important;
  }
`;

export const SliderArrow = styled.div`
  height: 100px;
  width: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4;
  cursor: pointer;

  i {
    font-size: ${theme.fontSizes[6]};
  }
`;
